import {token} from "@atlaskit/tokens";
import {AnankeField} from "@vesta/ananke/components/anankeField";
import {AnankeLabel} from "@vesta/ananke/components/anankeLabel";
import {AnankeSelect} from "@vesta/ananke/components/anankeSelect";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {getVisibleCalendarDisplayMetricOptions} from "~/domainModels/calendarDisplayMetrics/types";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

const CALENDAR_CUSTOMIZE_MOBILE_DESCRIPTION_STYLE = {
    color: token("color.text.subtle"),
    fontFeatureSettings: "'liga' off, 'calt' off",
    fontFamily: "Inter",
    fontSize: "var(--Font-Size-Body-S, 12px)",
    fontStyle: "normal",
    fontWeight: "var(--Font-Weight-Body-Medium, 500)",
    lineHeight: "var(--Line-Height-Body-S, 16px)",
} as const;

const CALENDAR_CUSTOMIZE_MOBILE_LABEL_STYLE = {
    color: token("color.text.subtle"),
    fontFeatureSettings: "'liga' off, 'calt' off",
    fontFamily: "Inter",
    fontSize: "var(--Font-Size-Body-S, 12px)",
    fontStyle: "normal",
    fontWeight: "var(--Font-Weight-Body-Bold, 653)",
    lineHeight: "var(--Line-Height-Body-S, 16px)",
} as const;

export function CalendarCustomizeViewPanelBody<T extends {id: Uuid}>({
    controller,
    ephemeralMetricId,
    onEphemeralMetricIdChange,
    layout = "default",
}: {
    controller: OttopilotDataViewerController<T>;
    ephemeralMetricId: string;
    onEphemeralMetricIdChange: (metricId: string) => void;
    layout?: "default" | "mobile";
}) {
    const metricsConfig = controller.calendarDisplayMetrics;
    if (metricsConfig === undefined) {
        return null;
    }

    const visibleOptions = getVisibleCalendarDisplayMetricOptions(
        metricsConfig,
        controller.calendarDisplayMetricContext ?? {},
    );
    const selectOptions = visibleOptions.map((option) => ({
        value: option.id,
        label: option.label,
    }));
    const selectedOption =
        selectOptions.find((option) => option.value === ephemeralMetricId) ?? selectOptions[0] ?? null;

    const metricSelect = (
        <AnankeSelect
            id="calendar-display-metric"
            value={selectedOption}
            options={selectOptions}
            onChange={(newValue: {value: string; label: string} | null) => {
                if (newValue === null) {
                    return;
                }
                onEphemeralMetricIdChange(newValue.value);
            }}
        />
    );

    if (layout === "mobile") {
        return (
            <div className="w-full min-w-0 self-stretch pt-ds-space-100 pb-ds-space-200 grid gap-ds-space-200">
                <p style={CALENDAR_CUSTOMIZE_MOBILE_DESCRIPTION_STYLE}>
                    Selecting these metrics helps customize your calendar view based on the chosen preferences.
                </p>
                <div className="flex flex-col items-start self-stretch w-full min-w-0 gap-ds-space-050">
                    <AnankeLabel
                        htmlFor="calendar-display-metric"
                        isRequired={true}
                        style={CALENDAR_CUSTOMIZE_MOBILE_LABEL_STYLE}
                    >
                        Metric
                    </AnankeLabel>
                    <div className="w-full min-w-0 self-stretch">{metricSelect}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="px-ds-space-250 py-ds-space-200 w-full min-w-0 grid gap-ds-space-200">
            <p className="text-sm text-ds-text-subtle">
                Selecting these metrics helps customize your calendar view based on the chosen preferences.
            </p>
            <AnankeField
                id="calendar-display-metric"
                label="Metric"
                isRequired={true}
            >
                {metricSelect}
            </AnankeField>
        </div>
    );
}

export function CalendarCustomizeViewPanelFooter({
    onCancel,
    onConfirm,
}: {
    onCancel: () => void;
    onConfirm: () => void;
}) {
    return (
        <div className="flex h-full w-full min-w-0 items-center justify-end gap-x-ds-space-100 px-ds-space-150">
            <AnankeButton
                type="button"
                appearance="subtle"
                onClick={onCancel}
            >
                Cancel
            </AnankeButton>
            <AnankeButton
                type="button"
                appearance="primary"
                onClick={onConfirm}
            >
                Confirm
            </AnankeButton>
        </div>
    );
}
